// client/src/pages/TeacherPortal.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import api from '../services/api';
import VanderbiltAssessmentForm from '../components/teacher-portal/VanderbiltAssessmentForm';

// Portal states
const STATES = {
  LOADING: 'loading',
  ERROR: 'error',
  CONSENT: 'consent',
  ASSESSMENT: 'assessment',
  COMPLETED: 'completed',
};

const TeacherPortal = () => {
  const { token } = useParams();

  const [state, setState] = useState(STATES.LOADING);
  const [portalData, setPortalData] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Consent form state
  const [consent, setConsent] = useState({
    teacherName: '',
    agreedToTerms: false,
  });

  useEffect(() => {
    loadPortalData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadPortalData = async () => {
    setState(STATES.LOADING);
    setError('');

    try {
      const data = await api.getTeacherPortalData(token);
      setPortalData(data);

      // Check status
      if (data.status === 'completed') {
        setState(STATES.COMPLETED);
      } else if (data.consentGiven) {
        // Consent already given, load assessment
        await loadAssessment();
      } else {
        // Need consent
        setState(STATES.CONSENT);
        // Pre-fill teacher name if available
        if (data.teacherName) {
          setConsent(prev => ({ ...prev, teacherName: data.teacherName }));
        }
      }
    } catch (err) {
      console.error('Error loading portal data:', err);
      setError(err.message || 'Failed to load portal. The link may be invalid or expired.');
      setState(STATES.ERROR);
    }
  };

  const loadAssessment = async () => {
    try {
      const assessmentKey = portalData?.assessmentKey || 'vanderbilt-adhd-teacher-rating-scale';
      const data = await api.startTeacherAssessment(token, assessmentKey);
      setAssessment(data.assessment);
      setState(STATES.ASSESSMENT);
    } catch (err) {
      console.error('Error loading assessment:', err);
      setError(err.message || 'Failed to load assessment');
      setState(STATES.ERROR);
    }
  };

  const handleConsentSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      if (!consent.agreedToTerms) {
        setError('Please agree to the terms to continue.');
        setSubmitting(false);
        return;
      }

      if (!consent.teacherName.trim()) {
        setError('Please enter your name.');
        setSubmitting(false);
        return;
      }

      await api.submitTeacherConsent(token, {
        teacherName: consent.teacherName.trim(),
        agreedToTerms: consent.agreedToTerms,
      });

      // Load assessment after consent
      await loadAssessment();
    } catch (err) {
      console.error('Error submitting consent:', err);
      setError(err.message || 'Failed to submit consent');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssessmentSubmit = async (responses) => {
    setSubmitting(true);
    setError('');

    try {
      const assessmentKey = portalData?.assessmentKey || 'vanderbilt-adhd-teacher-rating-scale';
      await api.submitTeacherAssessment(token, assessmentKey, responses);
      setState(STATES.COMPLETED);
    } catch (err) {
      console.error('Error submitting assessment:', err);
      setError(err.message || 'Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  // Render loading state
  if (state === STATES.LOADING) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="grey.100"
      >
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>Loading assessment portal...</Typography>
        </Paper>
      </Box>
    );
  }

  // Render error state
  if (state === STATES.ERROR) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="grey.100"
      >
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Typography color="text.secondary">
              If you believe this is an error, please contact the parent or guardian
              who sent you this invitation.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Render consent form
  if (state === STATES.CONSENT) {
    return (
      <Box bgcolor="grey.100" minHeight="100vh" py={4}>
        <Container maxWidth="md">
          {/* Header */}
          <Paper sx={{ p: 4, mb: 3 }}>
            <Box display="flex" alignItems="center" mb={3}>
              <SchoolIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
              <Box>
                <Typography variant="h4" component="h1">
                  Teacher Assessment Portal
                </Typography>
                <Typography color="text.secondary">
                  AIMHI Mental Health Platform
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              You&apos;ve been invited to complete an assessment
            </Typography>

            {portalData?.childName && (
              <Typography sx={{ mb: 2 }}>
                For student: <strong>{portalData.childName}</strong>
              </Typography>
            )}

            {portalData?.message && (
              <Card variant="outlined" sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Message from parent/guardian:
                  </Typography>
                  <Typography>{portalData.message}</Typography>
                </CardContent>
              </Card>
            )}

            <Typography variant="body1" sx={{ mb: 3 }}>
              This assessment (Vanderbilt ADHD Teacher Rating Scale) helps clinicians
              understand how a student is functioning in the classroom. Your observations
              are valuable in supporting the student&apos;s care.
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Time Required:</strong> Approximately 10-15 minutes
              </Typography>
              <Typography variant="body2">
                <strong>What&apos;s Assessed:</strong> Attention, activity levels, and classroom behavior
              </Typography>
            </Alert>
          </Paper>

          {/* Consent Form */}
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom>
              Consent and Agreement
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleConsentSubmit}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  By completing this assessment, you acknowledge that:
                </Typography>
                <ul>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      Your observations will be shared with the student&apos;s healthcare provider
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      This information will be used to support the student&apos;s treatment
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      Your responses are confidential and will be handled according to HIPAA guidelines
                    </Typography>
                  </li>
                </ul>
              </Box>

              <TextField
                label="Your Full Name"
                value={consent.teacherName}
                onChange={(e) => setConsent(prev => ({ ...prev, teacherName: e.target.value }))}
                fullWidth
                required
                sx={{ mb: 3 }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={consent.agreedToTerms}
                    onChange={(e) => setConsent(prev => ({ ...prev, agreedToTerms: e.target.checked }))}
                  />
                }
                label="I agree to provide my observations and understand how this information will be used."
              />

              <Box display="flex" justifyContent="flex-end" mt={3}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={submitting ? <CircularProgress size={20} /> : <AssignmentIcon />}
                  disabled={submitting}
                >
                  {submitting ? 'Processing...' : 'Continue to Assessment'}
                </Button>
              </Box>
            </form>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Render assessment
  if (state === STATES.ASSESSMENT) {
    return (
      <Box bgcolor="grey.100" minHeight="100vh" py={4}>
        <Container maxWidth="md">
          <Paper sx={{ p: 4, mb: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <SchoolIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
              <Typography variant="h5" component="h1">
                Vanderbilt ADHD Teacher Rating Scale
              </Typography>
            </Box>

            {portalData?.childName && (
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Completing for: {portalData.childName}
              </Typography>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
          </Paper>

          <VanderbiltAssessmentForm
            assessment={assessment}
            onSubmit={handleAssessmentSubmit}
            submitting={submitting}
          />
        </Container>
      </Box>
    );
  }

  // Render completed state
  if (state === STATES.COMPLETED) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="grey.100"
      >
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Thank You!
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              Your assessment has been submitted successfully.
            </Typography>

            {portalData?.childName && (
              <Typography sx={{ mb: 2 }}>
                Your observations for <strong>{portalData.childName}</strong> have been
                recorded and will be reviewed by their healthcare provider.
              </Typography>
            )}

            <Typography color="text.secondary">
              You may close this window. If you have any questions, please contact
              the parent or guardian who sent you this invitation.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return null;
};

export default TeacherPortal;
